import React, {useEffect, useState} from "react";
import {Avatar, Badge, Stack, Typography} from "@mui/material";
import dayjs from "dayjs";
import SparkleIcon from "~/icons/svg/SparkleIcon.svg?react";
import DatabaseIcon from "~/icons/svg/DatabaseIcon.svg?react";
import ClarifyIcon from "~/icons/svg/ClarifyIcon.svg?react";
import DangerIcon from "~/icons/svg/DangerIcon.svg?react";
import InfoIcon from "~/icons/svg/InfoIcon.svg?react";
import {Tag} from "~/components/tag/Tag";
import type {AgentMetadata, ChatMessage} from "~/pages/chat-agent/types/chatTypes";
import {type TurnOutcome, turnAuthors, turnOutcome, turnUsedTools} from "~/pages/chat-agent/utils/chatBlocks";
import {agentLabel} from "~/pages/chat-agent/utils/toolLabels";
import {
    doneBadgeSx,
    elapsedTimerSx,
    getAvatarSx,
    getOutcomeTagSx,
    type OutcomeTone,
    turnHeadSx,
    turnMetaSx,
    turnTitleSx,
    workingTitleSx,
} from "~/pages/chat-agent/components/AgentActivity/agentActivity.styles";

const OUTCOMES: Record<TurnOutcome, {icon: React.ReactNode; label: string; tone: OutcomeTone}> = {
    complete: {icon: <DatabaseIcon/>, label: "Complete", tone: "good"},
    clarify: {icon: <ClarifyIcon/>, label: "Needs a detail", tone: "info"},
    failed: {icon: <DangerIcon/>, label: "Failed", tone: "error"},
    stopped: {icon: <InfoIcon/>, label: "Stopped by user", tone: "plain"},
};

/** `4s`, `1m 04s`. */
function formatElapsed(ms: number): string {
    const s = Math.max(0, Math.floor(ms / 1000));
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

/** `Today 15:06`, `Yesterday 16:42`, `27 Jul 2026 09:10`. */
function formatFinishedAt(date: Date): string {
    const finished = dayjs(date);
    const time = finished.format("HH:mm");
    if (finished.isSame(dayjs(), "day")) return `Today ${time}`;
    if (finished.isSame(dayjs().subtract(1, "day"), "day")) return `Yesterday ${time}`;
    return finished.format("D MMM YYYY HH:mm");
}

type Props = {
    /** The chat's agent (`app_name`); names the turn until a reply says which agent is working. */
    appName: string;
    /** From agents-metadata, for display names. */
    agents?: AgentMetadata[];
    question?: ChatMessage;
    /** Everything the agents sent for this question so far. */
    replies: ChatMessage[];
    /** The agents are still working on this answer. */
    isActive: boolean;
};

/**
 * First row of an agent turn. While working: which agent is working and a live timer. Once done: every
 * agent that contributed, an outcome pill, when it finished and how long it worked.
 */
export const AgentTurnHead: React.FC<Props> = ({appName, agents, question, replies, isActive}) => {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!isActive) return;
        const id = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(id);
    }, [isActive]);

    const authors = turnAuthors(replies);

    if (isActive) {
        const workingAgent = replies.findLast(r => r.type === "bot" && r.author)?.author ?? appName;
        return (
            <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                className="chat-message"
                sx={turnHeadSx}
                aria-busy="true"
            >
                <Avatar variant="rounded" sx={getAvatarSx(true)}>
                    <SparkleIcon/>
                </Avatar>
                <Typography variant="body2" sx={workingTitleSx}>
                    {`${agentLabel(workingAgent, agents)} working`}
                </Typography>
                {question && (
                    <Typography variant="body2" role="timer" sx={elapsedTimerSx}>
                        {formatElapsed(now - question.timestamp.getTime())}
                    </Typography>
                )}
            </Stack>
        );
    }

    const outcome = turnOutcome(replies);
    const {icon, label, tone} = OUTCOMES[outcome];
    // An answer that used no tools retrieved no data.
    const avatarIcon = outcome === "complete" && !turnUsedTools(replies) ? <SparkleIcon/> : icon;
    const endedAt = replies[replies.length - 1]?.timestamp;
    const workedMs = endedAt && question ? endedAt.getTime() - question.timestamp.getTime() : undefined;

    return (
        <Stack direction="row" alignItems="center" flexWrap="wrap" className="chat-message" sx={turnHeadSx}>
            <Badge
                variant="dot"
                overlap="rectangular"
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                invisible={outcome !== "complete"}
                sx={doneBadgeSx}
            >
                <Avatar variant="rounded" sx={getAvatarSx(false)}>
                    {avatarIcon}
                </Avatar>
            </Badge>
            <Typography variant="body2" sx={turnTitleSx}>
                {(authors.length ? authors : [appName]).map(a => agentLabel(a, agents)).join(", ")}
            </Typography>
            <Tag label={label} color="default" sx={getOutcomeTagSx(tone)}/>
            {endedAt && (
                <Typography variant="caption" sx={turnMetaSx}>
                    {formatFinishedAt(endedAt)}
                    {workedMs != null
                        && ` · ${outcome === "stopped" ? "stopped after" : "worked for"} ${formatElapsed(workedMs)}`}
                </Typography>
            )}
        </Stack>
    );
};
